import React, { useCallback, useEffect, useRef, useState } from "react";
import { Icon } from "@virkesborsen/ui-component-library";

export default function ComparisonSlider({
  leftImage,
  rightImage,
  rightImageText,
  leftImageText,
}) {
  const [isResizing, setIsResizing] = useState(false);
  const [sliderPosition, setSliderPosition] = useState(50);
  const topImageRef = useRef();
  const handleRef = useRef();
  const containerRef = useRef();

  const setPositioning = useCallback((x) => {
    if (!containerRef.current) return;

    const { left, width } = containerRef.current.getBoundingClientRect();
    const handleWidth = handleRef.current?.offsetWidth || 0;

    if (x >= left && x <= left + width - handleWidth) {
      const percentage = ((x - left) / width) * 100;
      setSliderPosition(percentage);
    }
  }, []);

  const handleResize = useCallback(
    (e) => {
      if (e.clientX) {
        setPositioning(e.clientX);
      } else if (e.touches?.[0]?.clientX) {
        setPositioning(e.touches[0].clientX);
      }
    },
    [setPositioning]
  );

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    window.removeEventListener("mousemove", handleResize);
    window.removeEventListener("touchmove", handleResize);
    window.removeEventListener("mouseup", handleResizeEnd);
    window.removeEventListener("touchend", handleResizeEnd);
  }, [handleResize]);

  const onKeyDown = useCallback(
    (e) => {
      if (!containerRef.current || !handleRef.current) return;

      const { left, width } = containerRef.current.getBoundingClientRect();
      const currentX = left + (width * sliderPosition) / 100;

      if (e.code === "ArrowLeft") {
        setPositioning(currentX - 10);
      }
      if (e.code === "ArrowRight") {
        setPositioning(currentX + 10);
      }
    },
    [setPositioning, sliderPosition]
  );

  useEffect(() => {
    if (isResizing) {
      window.addEventListener("mousemove", handleResize);
      window.addEventListener("touchmove", handleResize);
      window.addEventListener("mouseup", handleResizeEnd);
      window.addEventListener("touchend", handleResizeEnd);
    }
    return () => {
      window.removeEventListener("mousemove", handleResize);
      window.removeEventListener("touchmove", handleResize);
      window.removeEventListener("mouseup", handleResizeEnd);
      window.removeEventListener("touchend", handleResizeEnd);
    };
  }, [isResizing, handleResize, handleResizeEnd]);

  useEffect(() => {
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onKeyDown]);

  useEffect(() => {
    // Center slider on load
    if (!containerRef.current) return;

    const { left, width } = containerRef.current.getBoundingClientRect();
    setPositioning(left + width / 2);
  }, [setPositioning]);

  return (
    <div
      ref={containerRef}
      className="mx-auto relative h-full w-full overflow-hidden aspect-video shadow-[0_15px_15px_rgba(0,0,0,0.25)]"
    >
      {/* Bottom image */}
      <div className="absolute w-full h-full overflow-hidden">
        <img
          src={rightImage}
          alt="Bottom image"
          className="w-full h-full align-middle select-none pointer-events-none object-cover"
        />
        {rightImageText && (
          <p className="absolute top-0 right-0 text-white p-2 bg-black/50 rounded text-[12px] mb-0">
            {rightImageText}
          </p>
        )}
      </div>

      {/* Top image */}
      <div
        ref={topImageRef}
        className="absolute w-full h-full overflow-hidden z-[2]"
        style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
      >
        <img
          src={leftImage}
          alt="Top image"
          className="w-full h-full align-middle select-none pointer-events-none object-cover"
        />
        {leftImageText && (
          <p className="absolute top-0 left-0 text-white p-2 bg-black/50 rounded text-[12px] mb-0">
            {leftImageText}
          </p>
        )}
      </div>

      <div
        ref={handleRef}
        className="absolute w-[3px] h-full bg-white z-[3] cursor-col-resize top-0 flex items-center justify-center"
        style={{
          left: `${sliderPosition}%`,
          transform: "translateX(-0%)",
        }}
        onMouseDown={() => setIsResizing(true)}
        onTouchStart={() => setIsResizing(true)}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-1.5 flex gap-1">
          <Icon
            tw
            name="ArrowShortSimple"
            size="small"
            className="rotate-180"
          />
          <Icon tw name="ArrowShortSimple" size="small" />
        </div>
      </div>
    </div>
  );
}

ComparisonSlider.defaultProps = {
  leftImage: "",
  leftImageText: "Left Image",
  rightImage: "",
  rightImageText: "Right image",
};
